import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, companyApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { Field, Select, TextArea } from '../components/Field.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type {
  Company,
  CompanyDocument,
  Json,
  Question,
  QuestionCategory,
  Submission,
} from '../lib/types.ts';
import {
  activeAdoptionTargets,
  computeProgress,
  questionCompletion,
} from '../lib/visibility.ts';
import { missingCompanyInfoFields } from '@l2u/shared/progress';

export function Questionnaire() {
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => companyApi.get<{ company: Company }>('/api/me'),
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['me', 'submissions'],
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>('/api/me/documents'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories'],
    queryFn: () =>
      companyApi.get<{ categories: QuestionCategory[] }>('/api/me/question-categories'),
  });

  const submitMutation = useMutation({
    mutationFn: () => companyApi.post('/api/me/submit'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
    },
  });

  const questions = questionsQuery.data?.questions ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const documents = documentsQuery.data?.documents ?? [];
  const categories = categoriesQuery.data?.categories ?? [];

  const docMap = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);
  const subByQ = useMemo(() => {
    const m = new Map<string, Submission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  const breakdown = useMemo(
    () => computeProgress(questions, submissions),
    [questions, submissions],
  );

  const missingInfoCount = useMemo(
    () => (meQuery.data?.company ? missingCompanyInfoFields(meQuery.data.company).length : 0),
    [meQuery.data?.company],
  );

  const visibleQuestions = useMemo(
    () => questions.filter((q) => breakdown.visibleIds.has(q.id)),
    [questions, breakdown],
  );

  const byCategory = useMemo(() => {
    const groups = new Map<string, Question[]>();
    for (const cat of categories) groups.set(cat.id, []);
    for (const q of visibleQuestions) {
      const list = groups.get(q.category_id);
      if (list) list.push(q);
      else groups.set(q.category_id, [q]);
    }
    return groups;
  }, [visibleQuestions, categories]);

  if (
    questionsQuery.isLoading ||
    submissionsQuery.isLoading ||
    documentsQuery.isLoading ||
    categoriesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }

  if (visibleQuestions.length === 0) {
    return <EmptyState title="No questions yet" description="Check back once your administrator has set up the questionnaire." />;
  }

  return (
    <div className="space-y-6">
      <ProgressBar value={breakdown.progress} label={`${breakdown.completedRequired}/${breakdown.totalRequired} required complete`} />

      <div className="space-y-8">
        {categories.map((cat) => {
          const list = byCategory.get(cat.id) ?? [];
          if (list.length === 0) return null;
          return (
            <section key={cat.id} className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">{cat.name}</h2>
              {list.map((q) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  submission={subByQ.get(q.id)}
                  documents={docMap}
                  documentsList={documents}
                />
              ))}
            </section>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-slate-900">Ready to submit?</div>
            <div className="mt-1 text-xs text-slate-500">
              All required questions ({breakdown.completedRequired}/{breakdown.totalRequired}) must
              be complete before submission.
            </div>
            {missingInfoCount > 0 && (
              <div className="mt-2 text-xs text-amber-700">
                {missingInfoCount} required company information field
                {missingInfoCount === 1 ? '' : 's'} still missing —{' '}
                <Link to="/c/info" className="font-medium underline">
                  complete company info
                </Link>
                .
              </div>
            )}
          </div>
          <Btn
            onClick={() => submitMutation.mutate()}
            loading={submitMutation.isPending}
            disabled={breakdown.progress < 100 || missingInfoCount > 0}
          >
            Submit for review
          </Btn>
        </div>
        {submitMutation.error && (
          <div className="mt-3"><ErrorMessage error={submitMutation.error} /></div>
        )}
        {submitMutation.isSuccess && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Submitted for review.
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  submission,
  documents,
  documentsList,
}: {
  question: Question;
  submission: Submission | undefined;
  documents: Map<string, CompanyDocument>;
  documentsList: CompanyDocument[];
}) {
  const completion = questionCompletion(question, submission);
  const adoptionTargets = activeAdoptionTargets(question, submission);
  const locked = submission?.status === 'approved';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">
            {question.label}
            {question.required && <span className="ml-1 text-red-500">*</span>}
          </div>
          {question.help_text && (
            <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
          )}
        </div>
        {submission && <SubmissionStatusBadge status={submission.status} />}
      </div>

      {submission?.reviewer_comment && submission.status === 'rejected' && (
        <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
          <div className="font-medium">Changes requested</div>
          <div>{submission.reviewer_comment}</div>
        </div>
      )}

      <AnswerInput question={question} submission={submission} locked={locked} />

      {question.type === 'multiselect_uploads' ? (
        <MultiselectUploadsEditor
          question={question}
          submission={submission}
          documents={documents}
          disabled={locked}
        />
      ) : (
        <>
          {(question.requires_upload ||
            question.upload_label ||
            (question.type === 'document_upload' && !question.adoptable_doc_id)) && (
            <FilesEditor question={question} submission={submission} disabled={locked} />
          )}

          {adoptionTargets.length > 0 && (
            <AdoptionsEditor
              question={question}
              submission={submission}
              targets={adoptionTargets}
              documents={documents}
              documentsList={documentsList}
              disabled={locked}
            />
          )}
        </>
      )}

      {completion && (
        <div className="mt-3 text-xs text-slate-500">
          {completionMessage(completion)}
        </div>
      )}
    </div>
  );
}

function completionMessage(reason: 'no_answer' | 'upload_required' | 'adoption_required'): string {
  switch (reason) {
    case 'no_answer': return '⏳ Awaiting your answer';
    case 'upload_required': return '⏳ A file upload is required';
    case 'adoption_required': return '⏳ Adopt or upload the linked policy';
  }
}

// ----------------------------------------------------------------------
// Answer input
// ----------------------------------------------------------------------

function AnswerInput({
  question,
  submission,
  locked,
}: {
  question: Question;
  submission: Submission | undefined;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<Json | null | undefined>(submission?.answer);
  const [comment, setComment] = useState(submission?.comment ?? '');
  const [touched, setTouched] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (input: { answer: Json | null; comment: string | null }) =>
      companyApi.put(`/api/me/submissions/${question.id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      setTouched(false);
    },
  });

  function commit(answer: Json | null) {
    saveMutation.mutate({ answer, comment: comment.trim() || null });
  }

  if (question.type === 'document_adopt' || question.type === 'document_upload') {
    return null; // handled by AdoptionsEditor / FilesEditor below
  }

  if (question.type === 'signature') {
    return (
      <SignatureInput
        submission={submission}
        locked={locked}
        saving={saveMutation.isPending}
        onSave={(value) => {
          setLocal(value as unknown as Json);
          commit(value as unknown as Json);
        }}
        onClear={() => {
          setLocal(null);
          commit(null);
        }}
      />
    );
  }

  if (question.type === 'yesno') {
    const value = (local ?? submission?.answer) as boolean | null | undefined;
    return (
      <div className="flex gap-2">
        {[
          { v: true, label: 'Yes' },
          { v: false, label: 'No' },
        ].map((o) => (
          <button
            key={String(o.v)}
            disabled={locked}
            onClick={() => {
              setLocal(o.v);
              commit(o.v);
            }}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              value === o.v
                ? 'border-transparent text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            } ${locked ? 'opacity-60' : ''}`}
            style={value === o.v ? { backgroundColor: 'var(--brand-accent)' } : {}}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  if (question.type === 'select') {
    const value = (local ?? submission?.answer) as string | null | undefined;
    return (
      <Select
        disabled={locked}
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value || null;
          setLocal(v);
          commit(v);
        }}
      >
        <option value="">— Select —</option>
        {(question.options ?? []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </Select>
    );
  }

  if (question.type === 'multiselect' || question.type === 'multiselect_uploads') {
    const value = ((local ?? submission?.answer) as string[] | null | undefined) ?? [];
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {(question.options ?? []).map((o) => {
          const checked = value.includes(o);
          return (
            <label
              key={o}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                checked ? 'border-current' : 'border-slate-200'
              } ${locked ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}
              style={checked ? { color: 'var(--brand-accent)' } : {}}
            >
              <input
                type="checkbox"
                disabled={locked}
                checked={checked}
                onChange={() => {
                  const next = checked ? value.filter((v) => v !== o) : [...value, o];
                  setLocal(next);
                  commit(next);
                }}
              />
              <span className="text-slate-700">{o}</span>
            </label>
          );
        })}
      </div>
    );
  }

  // text
  const value = (local ?? submission?.answer ?? '') as string;
  return (
    <div>
      <TextArea
        disabled={locked}
        value={String(value)}
        onChange={(e) => {
          setLocal(e.target.value);
          setTouched(true);
        }}
        onBlur={() => {
          if (touched) commit(value);
        }}
      />
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          placeholder="Optional comment"
          value={comment}
          disabled={locked}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => {
            if (comment !== (submission?.comment ?? '')) {
              commit(value);
            }
          }}
          className="block w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm placeholder:text-slate-400"
        />
        {saveMutation.isPending && <span className="text-xs text-slate-400">Saving…</span>}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Signature
// ----------------------------------------------------------------------

type SignatureAnswerValue = {
  signature_data_url: string;
  signer_name: string;
  signed_at: string;
};

function isSignatureAnswer(value: unknown): value is SignatureAnswerValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.signature_data_url === 'string' &&
    typeof v.signer_name === 'string' &&
    typeof v.signed_at === 'string'
  );
}

function SignatureInput({
  submission,
  locked,
  saving,
  onSave,
  onClear,
}: {
  submission: Submission | undefined;
  locked: boolean;
  saving: boolean;
  onSave: (value: SignatureAnswerValue) => void;
  onClear: () => void;
}) {
  const existing = isSignatureAnswer(submission?.answer) ? submission!.answer : null;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasStrokeRef = useRef(false);
  const [signerName, setSignerName] = useState(existing?.signer_name ?? '');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function startStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (locked) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointerPos(e);
  }

  function drawStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const last = lastPointRef.current;
    if (!canvas || !ctx || !last) return;
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    hasStrokeRef.current = true;
    if (!hasDrawn) setHasDrawn(true);
  }

  function endStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const ratio = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasStrokeRef.current = false;
    setHasDrawn(false);
  }

  function handleSave() {
    setError(null);
    if (!hasStrokeRef.current) {
      setError('Please draw your signature.');
      return;
    }
    const name = signerName.trim();
    if (!name) {
      setError('Please type your name.');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSave({
      signature_data_url: dataUrl,
      signer_name: name,
      signed_at: new Date().toISOString(),
    });
  }

  function handleClear() {
    setError(null);
    clearCanvas();
    setSignerName('');
    onClear();
  }

  if (existing) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <img
            src={existing.signature_data_url}
            alt="Signature"
            className="h-32 w-full object-contain"
          />
        </div>
        <div className="text-sm text-slate-700">
          <div>
            Signed by <span className="font-medium">{existing.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">
            {new Date(existing.signed_at).toLocaleString()}
          </div>
        </div>
        {!locked && (
          <Btn variant="ghost" size="sm" onClick={handleClear} loading={saving}>
            Clear &amp; re-sign
          </Btn>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 text-xs font-medium text-slate-600">
          Draw your signature below
        </div>
        <canvas
          ref={canvasRef}
          onPointerDown={startStroke}
          onPointerMove={drawStroke}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          className="h-40 w-full touch-none rounded-lg border border-dashed border-slate-300 bg-white"
          style={{ cursor: locked ? 'not-allowed' : 'crosshair' }}
        />
      </div>
      <Field label="Full name" required>
        <input
          type="text"
          value={signerName}
          disabled={locked}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="Type your full name"
          className="block w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm placeholder:text-slate-400"
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Btn size="sm" onClick={handleSave} loading={saving} disabled={locked}>
          Save signature
        </Btn>
        <Btn
          size="sm"
          variant="ghost"
          onClick={() => {
            setError(null);
            clearCanvas();
          }}
          disabled={locked || !hasDrawn}
        >
          Clear drawing
        </Btn>
        <span className="text-xs text-slate-400">
          Signed-at timestamp is recorded automatically.
        </span>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------
// Files
// ----------------------------------------------------------------------

function FilesEditor({
  question,
  submission,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const files = submission?.submission_files ?? [];

  async function uploadFiles(list: FileList) {
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `/api/me/submissions/${question.id}/upload-url`,
          { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        await companyApi.post(`/api/me/submissions/${question.id}/files`, {
          file_path: presigned.path,
          file_name: file.name,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
        });
      }
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  async function deleteFile(fileId: string) {
    setError(null);
    try {
      await companyApi.delete(`/api/me/submissions/${question.id}/files/${fileId}`);
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
    } catch (err) {
      setError(err);
    }
  }

  async function downloadFile(fileId: string) {
    const res = await companyApi.get<{ url: string }>(
      `/api/me/submissions/${question.id}/files/${fileId}/download-url`,
    ).catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  return (
    <div className="mt-3">
      <Field label={question.upload_label ?? 'Attachments'} required={question.requires_upload}>
        <FileDrop
          onFiles={uploadFiles}
          disabled={disabled || uploading}
          hint={uploading ? 'Uploading…' : undefined}
        />
      </Field>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-1.5 text-sm">
              <button onClick={() => void downloadFile(f.id)} className="text-blue-600 hover:underline">
                📎 {f.file_name}
              </button>
              {!disabled && (
                <button onClick={() => void deleteFile(f.id)} className="text-xs text-red-600 hover:underline">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorMessage error={error} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Per-option uploads (multiselect_uploads)
// ----------------------------------------------------------------------

function MultiselectUploadsEditor({
  question,
  submission,
  documents,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  documents: Map<string, CompanyDocument>;
  disabled: boolean;
}) {
  const selected = (submission?.answer as string[] | null | undefined) ?? [];
  if (selected.length === 0) return null;

  const triggerByOption = new Map<string, { documentId: string; adoptOnly: boolean }>();
  for (const t of question.adoption_triggers) {
    triggerByOption.set(t.trigger_value, {
      documentId: t.document_id,
      adoptOnly: t.adopt_only,
    });
  }

  return (
    <div className="mt-4 space-y-3">
      {selected.map((opt) => (
        <OptionUploadRow
          key={opt}
          question={question}
          submission={submission}
          option={opt}
          trigger={triggerByOption.get(opt) ?? null}
          documents={documents}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function OptionUploadRow({
  question,
  submission,
  option,
  trigger,
  documents,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  option: string;
  trigger: { documentId: string; adoptOnly: boolean } | null;
  documents: Map<string, CompanyDocument>;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const files = (submission?.submission_files ?? []).filter((f) => f.option_key === option);
  const adoption = (submission?.submission_adoptions ?? []).find(
    (a) => a.option_key === option && trigger && a.document_id === trigger.documentId,
  );

  const adoptableDoc = trigger ? documents.get(trigger.documentId) : undefined;

  async function uploadFiles(list: FileList) {
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `/api/me/submissions/${question.id}/upload-url`,
          {
            file_name: file.name,
            mime_type: file.type || 'application/octet-stream',
            option_key: option,
          },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        await companyApi.post(`/api/me/submissions/${question.id}/files`, {
          file_path: presigned.path,
          file_name: file.name,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          option_key: option,
        });
      }
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  async function deleteFile(fileId: string) {
    setError(null);
    try {
      await companyApi.delete(`/api/me/submissions/${question.id}/files/${fileId}`);
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
    } catch (err) {
      setError(err);
    }
  }

  async function downloadFile(fileId: string) {
    const res = await companyApi
      .get<{ url: string }>(`/api/me/submissions/${question.id}/files/${fileId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function adopt() {
    if (!trigger) return;
    setError(null);
    try {
      await companyApi.put(
        `/api/me/submissions/${question.id}/adoptions/${trigger.documentId}`,
        { mode: 'adopted', option_key: option },
      );
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
    } catch (err) {
      setError(err);
    }
  }

  async function clearAdoption() {
    if (!trigger) return;
    try {
      await companyApi.delete(
        `/api/me/submissions/${question.id}/adoptions/${trigger.documentId}`,
      );
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
    } catch (err) {
      setError(err);
    }
  }

  async function downloadStandard() {
    if (!trigger) return;
    const res = await companyApi
      .get<{ url: string }>(`/api/me/documents/${trigger.documentId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  const adopted = adoption?.mode === 'adopted';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-slate-900">{option}</div>
        {trigger && adoptableDoc && (
          <button
            onClick={() => void downloadStandard()}
            className="text-xs text-blue-600 hover:underline"
          >
            View standard: {adoptableDoc.name}
          </button>
        )}
      </div>

      {trigger && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Btn
            size="sm"
            variant={adopted ? 'primary' : 'secondary'}
            disabled={disabled}
            onClick={() => void adopt()}
          >
            {adopted ? '✓ Adopted' : 'Adopt this policy'}
          </Btn>
          {adopted && !disabled && (
            <button
              onClick={() => void clearAdoption()}
              className="text-xs text-red-600 hover:underline"
            >
              Clear adoption
            </button>
          )}
          {!trigger.adoptOnly && <span className="text-xs text-slate-400">or upload below</span>}
        </div>
      )}

      {(!trigger || !trigger.adoptOnly) && (
        <div className="mt-3">
          <FileDrop
            onFiles={uploadFiles}
            disabled={disabled || uploading}
            hint={uploading ? 'Uploading…' : 'Upload one or more documents for this option'}
          />
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between rounded-md bg-white px-3 py-1.5 text-sm"
                >
                  <button
                    onClick={() => void downloadFile(f.id)}
                    className="text-blue-600 hover:underline"
                  >
                    📎 {f.file_name}
                  </button>
                  {!disabled && (
                    <button
                      onClick={() => void deleteFile(f.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ErrorMessage error={error} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Adoptions (document_adopt + adoption triggers)
// ----------------------------------------------------------------------

function AdoptionsEditor({
  question,
  submission,
  targets,
  documents,
  documentsList,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  targets: { documentId: string; adoptOnly: boolean }[];
  documents: Map<string, CompanyDocument>;
  documentsList: CompanyDocument[];
  disabled: boolean;
}) {
  return (
    <div className="mt-4 space-y-3">
      {targets.map((t) => (
        <DocumentAdoptionRow
          key={t.documentId}
          question={question}
          submission={submission}
          documentId={t.documentId}
          adoptOnly={t.adoptOnly}
          document={documents.get(t.documentId)}
          allDocs={documentsList}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function DocumentAdoptionRow({
  question,
  submission,
  documentId,
  adoptOnly,
  document,
  allDocs,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  documentId: string;
  adoptOnly: boolean;
  document: CompanyDocument | undefined;
  allDocs: CompanyDocument[];
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const adoption = submission?.submission_adoptions.find((a) => a.document_id === documentId);

  const adoptMutation = useMutation({
    mutationFn: () =>
      companyApi.put(`/api/me/submissions/${question.id}/adoptions/${documentId}`, {
        mode: 'adopted',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      companyApi.delete(`/api/me/submissions/${question.id}/adoptions/${documentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'submissions'] }),
  });

  async function downloadDoc() {
    const res = await companyApi.get<{ url: string }>(
      `/api/me/documents/${documentId}/download-url`,
    ).catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function uploadOwn(files: FileList) {
    setError(null);
    setUploading(true);
    try {
      const file = files.item(0);
      if (!file) return;
      const presigned = await companyApi.post<{ path: string; url: string }>(
        `/api/me/submissions/${question.id}/upload-url`,
        { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await companyApi.put(`/api/me/submissions/${question.id}/adoptions/${documentId}`, {
        mode: 'uploaded',
        file_path: presigned.path,
      });
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  const docName = document?.name ?? allDocs.find((d) => d.id === documentId)?.name ?? 'Document';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">{docName}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {adoptOnly ? 'You must adopt this standard policy.' : 'Adopt the standard policy or upload your own equivalent.'}
          </div>
        </div>
        <button
          onClick={() => void downloadDoc()}
          className="text-xs text-blue-600 hover:underline"
        >
          View standard
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn
          size="sm"
          variant={adoption?.mode === 'adopted' ? 'primary' : 'secondary'}
          disabled={disabled}
          loading={adoptMutation.isPending}
          onClick={() => adoptMutation.mutate()}
        >
          {adoption?.mode === 'adopted' ? '✓ Adopted' : 'Adopt this policy'}
        </Btn>
        {!adoptOnly && (
          <>
            <span className="text-xs text-slate-400">or</span>
            <label className={`text-xs ${disabled ? 'opacity-50' : 'cursor-pointer text-blue-600 hover:underline'}`}>
              <input
                type="file"
                className="hidden"
                disabled={disabled || uploading}
                onChange={(e) => {
                  if (e.target.files?.length) uploadOwn(e.target.files);
                  e.target.value = '';
                }}
              />
              {uploading ? 'Uploading…' : adoption?.mode === 'uploaded' ? 'Replace upload' : 'Upload your own'}
            </label>
            {adoption?.mode === 'uploaded' && adoption.file_path && (
              <span className="ml-2 text-xs text-slate-500">
                {adoption.file_path.split('/').pop()}
              </span>
            )}
          </>
        )}
        {adoption && !disabled && (
          <button
            onClick={() => deleteMutation.mutate()}
            className="ml-auto text-xs text-red-600 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
    </div>
  );
}
